import type { User } from '../interfaces/IUserRepository';
import type { Client } from '../interfaces/IClientRepository';
import type { OnboardingFlow } from '../interfaces/IOnboardingRepository';
import type { PlatformAdmin, AuditLog } from '../interfaces/IAdminRepository';

// Helper to generate UUIDs
const uuid = () => crypto.randomUUID();

// Helper for dates
const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

// Mock Clients
export const mockClients: Client[] = [
  {
    id: 'tenant-1',
    name: 'Acme Corporation',
    status: 'active',
    created_at: daysAgo(120),
    onboarding_completed: true,
    admin_unlocked: true,
    user_count: 12,
    last_activity: daysAgo(1),
    vat_number: 'MT12345678',
    primary_contact_email: 'john.smith@acme.com',
    company_legal_name: 'Acme Corporation Ltd',
    company_registration_number: 'C12345',
    legal_type: 'Private Limited Company',
    website: 'https://acme.com',
    billing_street: '123 Business Park',
    billing_city: 'Valletta',
    billing_postal_code: 'VLT 1234',
    billing_country: 'Malta',
    payment_method: 'Bank Transfer',
    bank_name: 'Bank of Valletta',
    bank_account_number: 'MT12VALL12345000000012345678901',
    bank_bic_code: 'VALLMTMT',
    po_on_invoices: true,
    mobile_phone: '79123456',
    mobile_phone_country: 'MT',
    vat_validated: true,
    vat_validated_at: daysAgo(119),
    email_verified: true,
    email_verified_at: daysAgo(120),
    bank_details_validated: true,
    registration_number_validated: true,
  },
  {
    id: 'tenant-2',
    name: 'TechStart Ltd',
    status: 'active',
    created_at: daysAgo(45),
    onboarding_completed: false,
    admin_unlocked: false,
    user_count: 3,
    last_activity: daysAgo(5),
    primary_contact_email: 'mike.wilson@techstart.com',
  },
  {
    id: 'tenant-3',
    name: 'Global Media Inc',
    status: 'active',
    created_at: daysAgo(30),
    onboarding_completed: true,
    admin_unlocked: true,
    user_count: 25,
    last_activity: daysAgo(0),
    vat_number: 'GB987654321',
    primary_contact_email: 'contact@globalmedia.com',
    company_legal_name: 'Global Media Inc',
    company_registration_number: 'GB123456',
    legal_type: 'Corporation',
    website: 'https://globalmedia.com',
    billing_street: '456 Media Street',
    billing_city: 'London',
    billing_postal_code: 'SW1A 1AA',
    billing_country: 'United Kingdom',
    payment_method: 'Direct Debit',
    bank_name: 'HSBC UK',
    bank_account_number: 'GB12HSBC12345678901234',
    bank_bic_code: 'HBUKGB4B',
    po_on_invoices: false,
    vat_validated: true,
    vat_validated_at: daysAgo(29),
    email_verified: true,
    email_verified_at: daysAgo(30),
    bank_details_validated: true,
    registration_number_validated: false,
  },
  {
    id: 'tenant-4',
    name: 'Retail Solutions',
    status: 'inactive',
    created_at: daysAgo(200),
    onboarding_completed: true,
    admin_unlocked: true,
    user_count: 8,
    last_activity: daysAgo(90),
  },
  {
    id: 'tenant-5',
    name: 'Creative Agency Co',
    status: 'active',
    created_at: daysAgo(15),
    onboarding_completed: false,
    admin_unlocked: false,
    user_count: 2,
    last_activity: daysAgo(10),
  },
];

// Mock Users
export const mockUsers: User[] = [
  {
    id: 'user-1',
    email: 'john.smith@acme.com',
    first_name: 'John',
    last_name: 'Smith',
    phone: '+1-555-0101',
    job_title: 'CEO & Founder',
    role: 'customer_admin',
    client_id: 'tenant-1',
    status: 'active',
    created_at: daysAgo(120),
    last_login: daysAgo(1),
  },
  {
    id: 'user-2',
    email: 'sarah.jones@acme.com',
    first_name: 'Sarah',
    last_name: 'Jones',
    phone: '+1-555-0102',
    job_title: 'Operations Manager',
    role: 'user_manager',
    client_id: 'tenant-1',
    status: 'active',
    created_at: daysAgo(110),
    last_login: daysAgo(2),
  },
  {
    id: 'user-3',
    email: 'mike.wilson@techstart.com',
    first_name: 'Mike',
    last_name: 'Wilson',
    phone: '+44-20-7946-0958',
    job_title: 'Account Executive',
    role: 'account_manager',
    client_id: 'tenant-2',
    status: 'active',
    created_at: daysAgo(45),
    last_login: daysAgo(5),
  },
  {
    id: 'user-4',
    email: 'emily.brown@global.com',
    first_name: 'Emily',
    last_name: 'Brown',
    phone: '+49-30-1234-5678',
    job_title: 'Managing Director',
    role: 'customer_admin',
    client_id: 'tenant-3',
    status: 'active',
    created_at: daysAgo(30),
    last_login: daysAgo(0),
  },
  {
    id: 'user-5',
    email: 'david.taylor@creative.com',
    first_name: 'David',
    last_name: 'Taylor',
    phone: '+33-1-42-86-82-00',
    job_title: 'CFO',
    role: 'finance_manager',
    client_id: 'tenant-5',
    status: 'active',
    created_at: daysAgo(15),
    last_login: daysAgo(10),
  },
];

// Mock Onboarding Flows
export const mockOnboardingFlows: OnboardingFlow[] = [
  {
    id: 'flow-1',
    tenant_id: 'tenant-1',
    client_id: 'tenant-1',
    flow_type: 'client',
    token: 'token-1',
    magic_token: 'magic-token-1',
    primary_email: 'john.smith@acme.com',
    status: 'completed',
    created_at: daysAgo(120),
    completed_at: daysAgo(110),
    account_manager_completed: true,
    finance_manager_completed: true,
    creative_manager_completed: true,
    account_owner_completed: true,
    overall_progress: 100,
    admin_unlocked: true,
    reminder_count: 0,
    reminders_sent: [],
    is_test_flow: false,
    created_via: 'api',
  },
  {
    id: 'flow-2',
    tenant_id: 'tenant-2',
    client_id: 'tenant-2',
    flow_type: 'client',
    token: 'token-2',
    magic_token: 'magic-token-2',
    primary_email: 'mike.wilson@techstart.com',
    status: 'in_progress',
    created_at: daysAgo(45),
    account_manager_completed: true,
    finance_manager_completed: true,
    creative_manager_completed: false,
    account_owner_completed: false,
    overall_progress: 50,
    admin_unlocked: false,
    reminder_count: 2,
    last_reminder_sent_at: daysAgo(3),
    reminders_sent: [
      { template: 'gentle', sent_at: daysAgo(7) },
      { template: 'urgent', sent_at: daysAgo(3) },
    ],
    admin_notes: 'Waiting for creator manager response',
    is_test_flow: false,
    created_via: 'api',
  },
  {
    id: 'flow-3',
    tenant_id: 'tenant-3',
    client_id: 'tenant-3',
    flow_type: 'client',
    token: 'token-3',
    magic_token: 'magic-token-3',
    primary_email: 'contact@globalmedia.com',
    status: 'completed',
    created_at: daysAgo(30),
    completed_at: daysAgo(25),
    account_manager_completed: true,
    finance_manager_completed: true,
    creative_manager_completed: true,
    account_owner_completed: true,
    overall_progress: 100,
    admin_unlocked: true,
    reminder_count: 1,
    reminders_sent: [
      { template: 'gentle', sent_at: daysAgo(28) },
    ],
    is_test_flow: false,
    created_via: 'api',
  },
  {
    id: 'flow-4',
    tenant_id: 'tenant-5',
    client_id: 'tenant-5',
    flow_type: 'client',
    token: 'token-4',
    magic_token: 'magic-token-4',
    primary_email: 'contact@creativeagency.com',
    status: 'in_progress',
    created_at: daysAgo(15),
    account_manager_completed: false,
    finance_manager_completed: true,
    creative_manager_completed: false,
    account_owner_completed: false,
    overall_progress: 25,
    admin_unlocked: false,
    reminder_count: 1,
    last_reminder_sent_at: daysAgo(8),
    reminders_sent: [
      { template: 'gentle', sent_at: daysAgo(8) },
    ],
    is_test_flow: false,
    created_via: 'api',
  },
];

// Mock Platform Admins
export const mockPlatformAdmins: PlatformAdmin[] = [
  {
    id: 'admin-1',
    user_id: 'admin-user-1',
    role: 'platform_admin',
    permissions: {
      can_manage_tenants: true,
      can_view_analytics: true,
      can_send_emails: true,
      can_modify_onboarding: true,
      can_impersonate_users: true,
      can_manage_admins: true,
    },
    created_at: daysAgo(365),
  },
];

// Mock Audit Logs
export const mockAuditLogs: AuditLog[] = [
  {
    id: uuid(),
    admin_user_id: 'admin-user-1',
    admin_email: 'admin@faces.com',
    action: 'send_manual_reminder',
    target_type: 'onboarding_flow',
    target_id: 'flow-2',
    details: { template: 'urgent' },
    ip_address: '192.168.1.1',
    created_at: daysAgo(3),
  },
  {
    id: uuid(),
    admin_user_id: 'admin-user-1',
    admin_email: 'admin@faces.com',
    action: 'update_admin_notes',
    target_type: 'onboarding_flow',
    target_id: 'flow-2',
    details: { notes: 'Waiting for creator manager response' },
    created_at: daysAgo(3),
  },
  {
    id: uuid(),
    admin_user_id: 'admin-user-1',
    admin_email: 'admin@faces.com',
    action: 'view_tenant_details',
    target_type: 'tenant',
    target_id: 'tenant-2',
    created_at: daysAgo(2),
  },
];

// Helper function to simulate realistic delays
export const simulateDelay = (ms: number = 300) => 
  new Promise(resolve => setTimeout(resolve, ms));

// Helper to simulate errors (10% chance)
export const maybeSimulateError = () => {
  if (Math.random() < 0.1) {
    throw new Error('Simulated network error');
  }
};
